import { CloseOutlined } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  Drawer,
  FormControlLabel,
  OutlinedInput,
  Stack,
  styled,
  Switch,
  TextField,
} from "@mui/material";
import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { cacheService, userService } from "../../../Core/Api/services";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { BaseMetadataType } from "../../../Core/Api/Types/base-metadata-type";
import { DbMetadataFieldInfos } from "../../../Core/Api/Types/db-metadata-field-infos";
import { DbUserBasicInfos } from "../../../Core/Api/Types/db-user-basic-infos";
import { IDbBaseMetadataValueInfos } from "../../../Core/Api/Types/i-db-base-metadata-value-infos";
import { MetadataFieldValueObjectTypes } from "../../../Core/Api/Types/metadata-field-value-object-types";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { PlatformLanguageCode } from "../../../Core/Api/Types/platform-language-code";
import { SynopsisMetadataInfos } from "../../../Core/Api/Types/synopsis-metadata-infos";
import { TitleMetadataInfos } from "../../../Core/Api/Types/title-metadata-infos";
import { ComputeMetadataBaseType } from "../../../Core/Utils/functions";
import {
  AddOrUpdateFilter,
  GetAssetIdsFilter,
  GetAssetStatusFilter,
  GetClassFilter,
  GetConfidentialityFilter,
  GetCreatedbyFilter,
  GetFilters,
  GetHideWithoutAssetFilter,
  GetProviderIdFilter,
  GetTitleCustomerIdsFilter,
  GetTypeFilter,
  RemoveAllFilters,
  RemoveFilterById,
  SetAssetIdsFilter,
  SetAssetStatusFilter,
  SetClassFilter,
  SetConfidentialityFilter,
  SetCreatedbyFilter,
  SetHideWithoutAssetFilter,
  SetProviderIdFilter,
  SetTitleCustomerIdsFilter,
  SetTypeFilter,
} from "../../../Redux/Reducers/redux-slice-filters";
import { useFiltersPanel } from "../../../Utils/Hooks/useFiltersPanel";
import { useMultipleSelection } from "../../../Utils/Hooks/useMultipleSelection";
import FormSection from "../../Forms/FormSection/form-section";
import { AclMatch, GetObjectStatusLabel, UserHasAcls } from "../../../Utils/helpers";
import { MetadataInput } from "../../MetadataInput/metadata-input-index";
import PanelHeader, { PanelHeaderProps } from "../../Panels/PanelHeader/panel-header";
import { SIDEBAR_WIDTH, SIDEBAR_WIDTH_CLOSED } from "../../Sidebar/sidebar";
import FilterAccordion from "../FilterAccordion/filters-accordion";
import StatusHistoryPanelFilter from "./status-history-panel-filter";
import { PlatformMode } from "../../../Core/Api/Types/platform-mode";
import { GetSidebarOpen } from "../../../Redux/Reducers/redux-slice-settings-users";
import DrawerWrapper from "../../Drawers/Drawer/drawer-wrapper";
import { useTranslation } from "react-i18next";

export const FILTERS_PANEL_WIDTH: number = 280;
export const FILTERS_PANEL_WIDTH_CLOSED = 0;

export interface FiltersPanelProps {
  openSidebar: boolean;
  layer: ObjectLayer | null;
  type: MetadataFieldValueObjectTypes;
  metadataFieldsOverride?: DbMetadataFieldInfos[];
  isDrive?: boolean;
}

const FiltersPanel: React.FC<FiltersPanelProps> = (props) => {
  const { t } = useTranslation();
  const { layer, type, metadataFieldsOverride, isDrive = false } = props;

  const [expandedType, setExpandedType] = React.useState(false);
  const [expandedClass, setExpandedClass] = React.useState(false);
  const [expandedConfidentiality, setExpandedConfidentiality] = React.useState(false);
  const [expandedAssetStatus, setExpandedAssetStatus] = React.useState(false);
  const [expandedHideWithoutAsset, setExpandedHideWithoutAsset] = React.useState(false);
  const [expandedTitleCustomerIds, setExpandedTitleCustomerIds] = React.useState(false);
  const [expandedAssetIds, setExpandedAssetIds] = React.useState(false);
  const [expandedCreatedBy, setExpandedCreatedBy] = React.useState(false);
  const [expandedProviderId, setExpandedProviderId] = React.useState(false);
  const { filtersPanelOpen, closeFiltersPanel } = useFiltersPanel();

  const [allUsers, setAllUsers] = React.useState<DbUserBasicInfos[]>([]);
  const [providerValues, setProviderValues] = React.useState<{ label: string; value: number }[]>([]);

  // Redux
  const dispatch = useDispatch();
  const selectedFilters = useSelector(GetFilters);
  const hideWithoutAssetFilter = useSelector(GetHideWithoutAssetFilter);
  const typeFilter = useSelector(GetTypeFilter);
  const classFilter = useSelector(GetClassFilter);
  const assetStatusFilter = useSelector(GetAssetStatusFilter);
  const assetIdsFilter = useSelector(GetAssetIdsFilter);
  const titleCustomerIdsFilter = useSelector(GetTitleCustomerIdsFilter);
  const confidentialityFilter = useSelector(GetConfidentialityFilter);
  const createdbyFilter = useSelector(GetCreatedbyFilter);
  const providerIdFilter = useSelector(GetProviderIdFilter);

  const openSidebar = useSelector(GetSidebarOpen);

  const [filterPrefix, setFilterPrefix] = React.useState<string>("");
  const [filterActive, setFilterActive] = React.useState(false);
  const [filterDriveOnly, setFilterDriveOnly] = React.useState<boolean>(isDrive);
  const [filters, setFilters] = React.useState<DbMetadataFieldInfos[]>([]);

  const metadataFields = React.useMemo(() => cacheService.GetMetadata(), []);
  let customerName = cacheService.GetCustomerExternalIdLabel();
  let mode = cacheService.GetPlatformMode();

  const {
    isSelected: isSelectedFilter,
    select: selectFilter,
    selected: allSelectedFilters,
    unselectList: unselectFilters,
  } = useMultipleSelection<number>();

  const clearAllFilters = useCallback(() => {
    setFilterPrefix("");
    dispatch(RemoveAllFilters());

    if (filterActive) setFilterActive(!filterActive);
  }, [filterActive]);

  const showActiveOnlyFilters: React.MouseEventHandler = () => {
    setFilterActive(!filterActive);
  };

  const showDriveMetadataOnly: React.MouseEventHandler = () => {
    setFilterDriveOnly(!filterDriveOnly);
  };

  const handleCloseIconClick: React.MouseEventHandler = () => {
    closeFiltersPanel();
  };

  const handleInputFilterChange = (
    value: any,
    id: number,
    _valueIndex?: number,
    _pushAndReplaceWhereCultureIds?: number[],
    dateType?: string
  ) => {
    const metadataField = filters.find((x) => x.id === id);
    if (!metadataField) return;

    if (value === null || value.length === 0) {
      console.debug("filterpanel: handle field removed = " + metadataField.tagName);

      dispatch(RemoveFilterById(id));
      return;
    }

    const baseType: BaseMetadataType = metadataField.baseType!;

    const metadataValue: IDbBaseMetadataValueInfos = {
      $type: ComputeMetadataBaseType(baseType),
      id: id,
      fieldType: baseType,
      value: null,
      creationTime: new Date().toISOString(),
      updateTime: null,
      fieldName: null,
      fieldLabel: null,
      targetLayer: layer ?? metadataField.layer,
      valueLabel: null,
    };

    switch (baseType) {
      case BaseMetadataType.DbDateTimeMetadataValueInfos:
        if (dateType) metadataValue.valueLabel = dateType;
        metadataValue.value = value;
        break;

      case BaseMetadataType.DbBoolMetadataValueInfos:
        metadataValue.value = value;
        break;

      case BaseMetadataType.DbStringMetadataValueInfos:
      case BaseMetadataType.DbLongMetadataValueInfos:
      case BaseMetadataType.DbDoubleMetadataValueInfos:
        metadataValue.value =
          baseType === BaseMetadataType.DbLongMetadataValueInfos ||
          baseType === BaseMetadataType.DbDoubleMetadataValueInfos
            ? value.value
              ? Number(value.value)
              : value
            : value;

        break;
      case BaseMetadataType.DbStringMultiMetadataValueInfos:
      case BaseMetadataType.DbLongMultiMetadataValueInfos:
        if (metadataField.values && metadataField.values.length > 0) metadataValue.value = value?.map((v) => v.value);
        else metadataValue.value = value;
        break;

      case BaseMetadataType.DbLanguageCodeMultiMetadataValueInfos:
      case BaseMetadataType.DbTerritoryCodeMultiMetadataValueInfos:
        const langs: PlatformLanguageCode[] = [];
        if (value) {
          value.forEach((val) => {
            langs.push({
              // $type: "VDM.AirLab.Sdk.Core.Database.Types.PlatformLanguageCode, VDM.AirLab.Sdk.Core",
              id: val.id,
              externalId: val.externalId,
              value: val.value,
              label: val.label,
              rfc5646: val.rfc5646,
            });
          });
        }

        metadataValue.value = langs;
        break;
      case BaseMetadataType.DbTerritoryCodeMetadataValueInfos:
      case BaseMetadataType.DbLanguageCodeMetadataValueInfos:
        const lang: PlatformLanguageCode = {
          // $type: "VDM.AirLab.Sdk.Core.Database.Types.PlatformLanguageCode, VDM.AirLab.Sdk.Core",
          id: value.id,
          externalId: value.externalId,
          value: value.value,
          label: value.label,
          rfc5646: value.rfc5646,
        };

        metadataValue.value = lang;
        break;
      case BaseMetadataType.DbTitlesMetadataValueInfos:
        const titles: TitleMetadataInfos[] = [];
        value.forEach((val) => {
          titles.push({
            text: val,
            languageCode: null,
          });
        });

        metadataValue.value = titles;
        break;

      case BaseMetadataType.DbSynopsesMetadataValueInfos:
        const newVal: SynopsisMetadataInfos[] = [];
        value.forEach((val) => {
          newVal.push({
            dataType: null,
            text: val,
            textShort: null,
            languageCode: null,
            languageLocalized: null,
            languageLocalizedCultureId: null,
          });
        });

        metadataValue.value = newVal;
        break;

      case BaseMetadataType.DbCastsMetadataValueInfos:
        let castValue = !value ? [] : value.map((v) => ({ name: null, lastname: v }));

        metadataValue.value = castValue;
        break;
      default:
        return;
    }

    console.debug("filterpanel: handle field changed = " + metadataField.tagName);

    dispatch(AddOrUpdateFilter(metadataValue));
  };

  const countFilterActiveValues = (id: number): number => {
    return selectedFilters.some((x) => x.id === id) ? 1 : 0;
  };

  const handleBackdropClick: React.MouseEventHandler = () => {
    closeFiltersPanel();
  };

  React.useEffect(() => {
    const fields = (
      metadataFieldsOverride
        ? metadataFieldsOverride
        : metadataFields?.filter(
            (e) =>
              (isDrive ||
                mode !== PlatformMode.Distribution ||
                JSON.stringify(e.objectClasses) !== JSON.stringify([ObjectClass.Drive])) &&
              (!layer || e.layer === layer) &&
              e.isSearchable &&
              (!e.isUsedOnBundleMaker ||
                e.baseType === BaseMetadataType.DbCastsMetadataValueInfos ||
                e.baseType === BaseMetadataType.DbSynopsesMetadataValueInfos) &&
              e.objectType === type &&
              (!isDrive || (e.objectClasses?.includes(ObjectClass.Drive) && filterDriveOnly) || !filterDriveOnly)
            // Know issue with DbLanguageCodeMultiMetadataValueInfos => valueObject need to be DbLanguageCode[] and not DbString! To be patched!
          )
    ).map((x) => new DbMetadataFieldInfos(x));

    // Allow multi select on DbStringMetadataValueInfos (convert to DbStringMultiMetadataValueInfos)
    // Enable for strings and languages
    fields
      .filter(
        (x) =>
          (x.baseType === BaseMetadataType.DbStringMetadataValueInfos ||
            x.baseType === BaseMetadataType.DbLanguageCodeMetadataValueInfos) &&
          x.values !== null &&
          x.values.length > 0
      )
      .forEach((v) => {
        if (v.baseType === BaseMetadataType.DbStringMetadataValueInfos)
          v.baseType = BaseMetadataType.DbStringMultiMetadataValueInfos;
        else if (v.baseType === BaseMetadataType.DbLanguageCodeMetadataValueInfos)
          v.baseType = BaseMetadataType.DbLanguageCodeMultiMetadataValueInfos;
      });

    const providerMetaIndex = fields.findIndex((x) => x.tagName === "labProviderId");
    if (providerMetaIndex > -1) {
      setProviderValues(
        fields
          .splice(providerMetaIndex, 1)[0]
          .values?.map((v) => ({ label: v.label || "N/A", value: Number(v.value) })) || []
      );
    }

    setFilters(fields);

    console.log("filterpanel: reload filter panel");
  }, [layer, type, metadataFields, metadataFieldsOverride, isDrive, filterDriveOnly]);

  React.useEffect(() => {
    if (
      !UserHasAcls(
        [AclTypes.aclBackofficeUserListView, AclTypes.aclOrderAssignFollowers, AclTypes.aclBackofficeAuditLogsView],
        AclMatch.OneAtLeast
      )
    )
      return;

    userService.GetAllUsersBasic().then((response) => {
      if (response && response.users) setAllUsers(response.users);
    });
  }, []);

  const _renderFilter = (filter: DbMetadataFieldInfos, padding: boolean = true) => (
    <Stack key={filter.id}>
      {filter.label?.toString().includesAllWords(filterPrefix) &&
        (!filterActive || (filterActive && selectedFilters.length > 0 && countFilterActiveValues(filter.id) > 0)) && (
          <FilterAccordion
            title={filter.label}
            nbActiveFilters={countFilterActiveValues(filter.id)}
            expanded={isSelectedFilter(filter.id)}
            onExpand={() => (!isSelectedFilter(filter.id) ? selectFilter(filter.id) : unselectFilters([filter.id]))}
            sx={!padding ? { p: 0 } : {}}
            key={filter.id}
          >
            <MetadataInput
              onChange={handleInputFilterChange}
              data={filter}
              value={
                filter.baseType === BaseMetadataType.DbDateTimeMetadataValueInfos
                  ? selectedFilters.find((x) => x.id === filter.id && x.valueLabel === "startDate")
                  : selectedFilters.find((x) => x.id === filter.id)
              }
              value2={selectedFilters.find((x) => x.id === filter.id && x.valueLabel === "endDate")}
              dateRange={true}
              castOrSynoTextField={true}
            />
          </FilterAccordion>
        )}
    </Stack>
  );

  return (
    <DrawerWrapper
      onClose={handleCloseIconClick}
      anchor="left"
      header={
        <>
          <PanelHeader
            title={t("common:filters")}
            onClose={handleCloseIconClick as PanelHeaderProps["onClose"]}
            placement="left"
          />
        </>
      }
      footerContent={
        <Button size="medium" variant="contained" color="primary" onClick={handleCloseIconClick} fullWidth>
          {t("button:submitFilters")}
        </Button>
      }
      open={filtersPanelOpen}
    >
      <Stack position="sticky" top="0" gap={2} pb={2}>
        <Stack direction="row" gap={1}>
          <OutlinedInput
            value={filterPrefix}
            className="filters-search"
            placeholder={t("field:placeholder.searchFilter")}
            onChange={(e) => setFilterPrefix(e.target.value.toLowerCase())}
            sx={{ flexGrow: 1 }}
          />
          {((selectedFilters && selectedFilters.length > 0) ||
            (typeFilter !== null && typeFilter.length > 0) ||
            (classFilter !== null && classFilter.length > 0 && !isDrive) ||
            (assetStatusFilter !== null && assetStatusFilter.length > 0) ||
            (assetIdsFilter !== null && assetIdsFilter.length > 0) ||
            hideWithoutAssetFilter === true ||
            confidentialityFilter === true ||
            (titleCustomerIdsFilter !== null && titleCustomerIdsFilter.length > 0)) && (
            <>
              <Button size="small" onClick={clearAllFilters} startIcon={<CloseOutlined />}>
                {t("button:clearFilters")}
              </Button>
            </>
          )}
        </Stack>

        <Stack direction="row" gap={1}>
          {((selectedFilters && selectedFilters.length > 0) ||
            (typeFilter !== null && typeFilter.length > 0) ||
            (classFilter !== null && classFilter.length > 0 && !isDrive) ||
            (assetStatusFilter !== null && assetStatusFilter.length > 0) ||
            (assetIdsFilter !== null && assetIdsFilter.length > 0) ||
            hideWithoutAssetFilter === true ||
            confidentialityFilter === true ||
            (titleCustomerIdsFilter !== null && titleCustomerIdsFilter.length > 0)) && (
            <>
              <FormControlLabel
                variant="outlined"
                sx={{ flexGrow: 1 }}
                control={
                  <Switch
                    sx={{ mr: 0.5 }}
                    size="small"
                    checked={filterActive}
                    onChange={(e) => setFilterActive(!filterActive)}
                  />
                }
                label={t("form:label.activeFiltersOnly")}
              />
            </>
          )}
          {isDrive && (
            <FormControlLabel
              variant="outlined"
              sx={{ flexGrow: 1 }}
              control={
                <Switch
                  sx={{ mr: 0.5 }}
                  size="small"
                  checked={filterDriveOnly}
                  onChange={(e) => setFilterDriveOnly(!filterDriveOnly)}
                />
              }
              label={t("field:label.driveFiltersOnly")}
            />
          )}
        </Stack>
      </Stack>
      <FiltersBody>
        {(!layer || (layer === ObjectLayer.Asset && type === MetadataFieldValueObjectTypes.DbAssetObjectInfos)) &&
          !filterDriveOnly && (
            <>
              <Stack>
                <FilterAccordion
                  title={t("common:type")}
                  nbActiveFilters={typeFilter !== null ? typeFilter.length : 0}
                  expanded={expandedType}
                  onExpand={() => setExpandedType(!expandedType)}
                >
                  <Autocomplete
                    value={typeFilter || []}
                    multiple
                    onChange={(e, value) => dispatch(SetTypeFilter(value))}
                    options={Object.values(ObjectType)}
                    sx={{ width: "100%" }}
                    id={"typeFilter"}
                    renderInput={(params) => <TextField sx={{ mt: "-24px" }} {...params} />}
                  />
                </FilterAccordion>
              </Stack>
              {!isDrive && (
                <Stack>
                  <FilterAccordion
                    title={t("common:class")}
                    nbActiveFilters={classFilter !== null ? classFilter.length : 0}
                    expanded={expandedClass}
                    onExpand={() => setExpandedClass(!expandedClass)}
                    noDivider={true}
                  >
                    <Autocomplete
                      value={classFilter || []}
                      multiple
                      onChange={(e, value) => dispatch(SetClassFilter(value))}
                      options={Object.values(ObjectClass)}
                      sx={{ width: "100%" }}
                      id={"classFilter"}
                      renderInput={(params) => <TextField sx={{ mt: "-24px" }} {...params} />}
                    />
                  </FilterAccordion>
                </Stack>
              )}
              <Stack>
                <FilterAccordion
                  title={t("common:status")}
                  nbActiveFilters={assetStatusFilter !== null ? assetStatusFilter.length : 0}
                  expanded={expandedAssetStatus}
                  onExpand={() => setExpandedAssetStatus(!expandedAssetStatus)}
                  noDivider={true}
                >
                  <Autocomplete
                    value={assetStatusFilter || []}
                    multiple
                    onChange={(e, value) => dispatch(SetAssetStatusFilter(value))}
                    options={Object.values(ObjectStatus).filter((x) => x !== ObjectStatus.Disabled)}
                    getOptionLabel={(options: ObjectStatus) => GetObjectStatusLabel(options)}
                    sx={{ width: "100%" }}
                    id={"statusFilter"}
                    renderInput={(params) => <TextField sx={{ mt: "-24px" }} {...params} />}
                  />
                </FilterAccordion>
              </Stack>
              <Stack>
                <FilterAccordion
                  title={t("component:filterAccordion.assetIds")}
                  nbActiveFilters={assetIdsFilter ? 1 : 0}
                  expanded={expandedAssetIds}
                  onExpand={() => setExpandedAssetIds(!expandedAssetIds)}
                  noDivider={allUsers.length > 0}
                >
                  <Autocomplete
                    value={assetIdsFilter || []}
                    multiple
                    freeSolo
                    onChange={(e, value) => dispatch(SetAssetIdsFilter(value))}
                    options={[]}
                    sx={{ width: "100%" }}
                    id={"assetidsFiltering"}
                    renderInput={(params) => (
                      <TextField
                        sx={{ mt: "-24px" }}
                        {...params}
                        placeholder={t("field:placeholder.assetIdsFiltering")}
                      />
                    )}
                  />
                </FilterAccordion>
              </Stack>
              <StatusHistoryPanelFilter />
              {allUsers.length > 0 && (
                <Stack>
                  <FilterAccordion
                    title={t("common:createdBy")}
                    nbActiveFilters={createdbyFilter && createdbyFilter.ids.length > 0 ? createdbyFilter.ids.length : 0}
                    expanded={expandedCreatedBy}
                    onExpand={() => setExpandedCreatedBy(!expandedCreatedBy)}
                    noDivider={true}
                  >
                    <Autocomplete
                      value={
                        createdbyFilter && createdbyFilter.ids.length > 0
                          ? allUsers.filter((x) => createdbyFilter.ids.indexOf(x.id) !== -1)
                          : []
                      }
                      multiple
                      onChange={(e, value) =>
                        dispatch(
                          SetCreatedbyFilter(
                            value && value.length > 0
                              ? { ids: value.map((v) => v.id), labels: value.map((v) => v.name) }
                              : null
                          )
                        )
                      }
                      getOptionLabel={(o) => o.firstName + " " + o.lastName + " (" + o.login + ")"}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      options={allUsers.sort((a, b) => {
                        if ((a.firstName ?? "") < (b.firstName ?? "")) return -1;
                        if ((a.firstName ?? "") > (b.firstName ?? "")) return 1;
                        if ((a.lastName ?? "") < (b.lastName ?? "")) return -1;
                        if ((a.lastName ?? "") > (b.lastName ?? "")) return 1;
                        return 0;
                      })}
                      renderOption={(props, option) => {
                        return (
                          <Box {...props} key={option.id} component="li">
                            {option.firstName + " " + option.lastName + " (" + option.login + ")"}
                          </Box>
                        );
                      }}
                      sx={{ width: "100%" }}
                      id={"createdbyFiltering"}
                      renderInput={(params) => (
                        <TextField
                          sx={{ mt: "-24px", wordBreak: "break-all", wordWrap: "break-word" }}
                          {...params}
                          placeholder={t("field:placeholder.searchForSomeUsers")}
                        />
                      )}
                    />
                  </FilterAccordion>
                </Stack>
              )}
              {providerValues && (
                <Stack>
                  <FilterAccordion
                    title={t("component:filterAccordion.title.providerId")}
                    nbActiveFilters={
                      providerIdFilter && providerIdFilter.ids.length > 0 ? providerIdFilter.ids.length : 0
                    }
                    expanded={expandedProviderId}
                    onExpand={() => setExpandedProviderId(!expandedProviderId)}
                    noDivider={true}
                  >
                    <Autocomplete
                      value={
                        providerIdFilter && providerIdFilter.ids.length > 0
                          ? providerValues.filter((x) => providerIdFilter.ids.includes(x.value))
                          : []
                      }
                      multiple
                      onChange={(e, value) =>
                        dispatch(
                          SetProviderIdFilter(
                            value && value.length > 0
                              ? { ids: value.map((v) => v.value), labels: value.map((v) => v.label) }
                              : null
                          )
                        )
                      }
                      getOptionLabel={(o) => o.label}
                      isOptionEqualToValue={(o, v) => o.value === v.value}
                      options={providerValues}
                      sx={{ width: "100%" }}
                      id={"providedByFiltering"}
                      renderInput={(params) => (
                        <TextField
                          sx={{ mt: "-24px", wordBreak: "break-all", wordWrap: "break-word" }}
                          {...params}
                          placeholder={t("field:placeholder.searchForSomeProviders")}
                        />
                      )}
                    />
                  </FilterAccordion>
                </Stack>
              )}
            </>
          )}
        {(!layer || (layer === ObjectLayer.Title && type === MetadataFieldValueObjectTypes.DbAssetObjectInfos)) && (
          <Stack sx={{ border: "none" }}>
            <FilterAccordion
              title={t("chip:label.hideWithoutAssets")}
              nbActiveFilters={hideWithoutAssetFilter ? 1 : 0}
              expanded={expandedHideWithoutAsset}
              onExpand={() => setExpandedHideWithoutAsset(!expandedHideWithoutAsset)}
              noDivider={true}
            >
              <FormControlLabel
                checked={hideWithoutAssetFilter}
                onChange={(e, value) => dispatch(SetHideWithoutAssetFilter(value))}
                control={<Switch />}
                label=""
              />
            </FilterAccordion>
          </Stack>
        )}
        {(!layer || type === MetadataFieldValueObjectTypes.DbAssetObjectInfos) && (
          <Stack sx={{ border: "none" }}>
            <FilterAccordion
              title={t("common:confidentiality")}
              nbActiveFilters={confidentialityFilter ? 1 : 0}
              expanded={expandedConfidentiality}
              onExpand={() => setExpandedConfidentiality(!expandedConfidentiality)}
              noDivider={true}
            >
              <FormControlLabel
                checked={confidentialityFilter}
                onChange={(e, value) => dispatch(SetConfidentialityFilter(value))}
                control={<Switch />}
                label=""
              />
            </FilterAccordion>
          </Stack>
        )}
        {(!layer ||
          (layer === ObjectLayer.Title &&
            (type === MetadataFieldValueObjectTypes.DbAssetObjectInfos ||
              type === MetadataFieldValueObjectTypes.DbOrderItemInfos))) &&
          customerName && (
            <Stack sx={{ border: "none" }}>
              <FilterAccordion
                title={customerName}
                nbActiveFilters={titleCustomerIdsFilter ? 1 : 0}
                expanded={expandedTitleCustomerIds}
                onExpand={() => setExpandedTitleCustomerIds(!expandedTitleCustomerIds)}
                noDivider={true}
              >
                <Autocomplete
                  value={titleCustomerIdsFilter || []}
                  multiple
                  freeSolo
                  onChange={(e, value) => dispatch(SetTitleCustomerIdsFilter(value))}
                  options={[]}
                  sx={{ width: "100%" }}
                  id={"statusFiltitleCustomerIdsFilterter"}
                  renderInput={(params) => (
                    <TextField
                      sx={{ mt: "-24px" }}
                      {...params}
                      placeholder={t("field:placeholder.enterSomeCustomerName", { name: customerName })}
                    />
                  )}
                />
              </FilterAccordion>
            </Stack>
          )}

        <Divider sx={{ mt: 0, mb: 2 }} />

        {layer
          ? filters
              ?.filter((x) => x.layer?.toString() == layer)
              .sort((a, b) => {
                if (a.label! < b.label!) return -1;
                else return 1;
              })
              .map((filter) => _renderFilter(filter))
          : Object.keys(ObjectLayer)
              ?.filter((objectLayer) => filters.some((filter) => filter.layer === objectLayer))
              .sort()
              .map((objectLayer) => {
                return (
                  <FormSection
                    title={objectLayer}
                    size="small"
                    sx={{ backgroundColor: "inherit", pl: 2, pr: 2, mb: 1 }}
                    key={objectLayer}
                  >
                    {filters
                      ?.filter((x) => x.layer?.toString() == objectLayer)
                      .sort((a, b) => {
                        if (a.label! < b.label!) return -1;
                        else return 1;
                      })
                      .map((filter) => _renderFilter(filter, false))}
                  </FormSection>
                );
              })}
      </FiltersBody>
      {/* <FiltersPanelBackdrop onClick={handleBackdropClick} open={filtersPanelOpen} openSidebar={openSidebar} /> */}
    </DrawerWrapper>
  );
};

export default FiltersPanel;

const FiltersPanelBackdrop = styled(Box, {
  shouldForwardProp: (propName) => propName !== "openSidebar",
})<{ open: boolean; openSidebar: boolean }>(({ open, theme, openSidebar }) => ({
  display: open ? "block" : "none",
  position: "fixed",
  zIndex: 1201,
  height: "100%",
  width: "100%",
  left: openSidebar ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_CLOSED,
  background: "rgba(0,0,0,0.5)",
  transition: theme.transitions.create(["opacity", "left"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  opacity: 0,
  ...(open && {
    opacity: 1,
  }),
}));

const FiltersPanelDrawer = styled(Drawer, {
  shouldForwardProp: (propName) => propName !== "open" && propName !== "openSidebar",
})<{ open: boolean; openSidebar: boolean }>(({ theme, open, openSidebar }) => ({
  ".MuiDrawer-paper": {
    zIndex: 1202,
    height: "100vh",
    overflow: "hidden",
    background: theme.palette.background.default,
    width: FILTERS_PANEL_WIDTH,
    marginLeft: openSidebar ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_CLOSED,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    ...(!open && {
      overflowX: "hidden",
      width: FILTERS_PANEL_WIDTH_CLOSED,
      borderRight: "none",
      transition: theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    }),
  },
  ".filters-search": {
    marginBottom: 20,
    marginLeft: 20,
    marginRight: 20,
  },
}));

const FiltersBody = styled(Stack)(({ theme }) => ({
  ...theme.helpers(theme).scrollableContainer,
  height: "auto",
  flex: "1 0 0",
  minHeight: 0,
  overflowY: "scroll",
}));
